import { TriangleDownIcon, TriangleUpIcon } from "@chakra-ui/icons";
import {
  Box,
  HStack,
  SystemStyleObject,
  TableColumnHeaderProps,
  Td,
  Text,
  Th,
} from "@chakra-ui/react";
import { Cell, Header, flexRender } from "@tanstack/react-table";

interface TableCellProps {
  cell: Cell<any, any>;
  textWrap: boolean;
  sx?: SystemStyleObject;
}

export default function TableCell({ cell, textWrap, sx }: TableCellProps) {
  const rendered = flexRender(cell.column.columnDef.cell, cell.getContext());
  return (
    <Td
      {...(cell.column.columnDef.meta?.cellProps || {})}
      sx={{
        overflowX: "scroll",
        position: "relative",
        width: cell.column.getSize(),
        ...(cell.column.columnDef.maxSize ? { maxWidth: cell.column.columnDef.maxSize } : {}),
        ...(cell.column.columnDef.minSize ? { minWidth: cell.column.columnDef.minSize } : {}),
        ...(cell.column.columnDef.meta?.sx ?? {}),
        ...{ ...sx },
        paddingX: "12px",
      }}
    >
      {typeof rendered === "string" ? (
        <Text {...(textWrap ? { whiteSpace: "normal" } : {})} color="fg.muted" width="100%">
          {rendered}
        </Text>
      ) : (
        rendered
      )}
    </Td>
  );
}

export interface TableHeaderProps extends TableColumnHeaderProps {
  header: Header<any, unknown>;
  sx?: SystemStyleObject;
}

export function StyledTh({ header, children, sx = {}, ...rest }: TableHeaderProps) {
  return (
    <Th
      colSpan={header.colSpan}
      {...(header.column.columnDef.meta?.headerProps || {})}
      sx={{
        position: "relative",
        width: header.getSize(),
        ...(header.column.columnDef.maxSize ? { maxWidth: header.column.columnDef.maxSize } : {}),
        ...(header.column.columnDef.minSize ? { minWidth: header.column.columnDef.minSize } : {}),
        ...(header.column.columnDef.meta?.sx ?? {}),
        paddingX: "12px",
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Th>
  );
}

export function TableHeader({ header, sx = {}, ...rest }: TableHeaderProps) {
  return header.column.getCanSort() ? (
    <StyledTh cursor="pointer" onClick={() => header.column.toggleSorting()} header={header} sx={sx} {...rest}>
      <HStack {...(header.column.columnDef.meta?.headerHStackProps || {})}>
        <Text color="chakra-subtle-text">
          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
        </Text>
        {header.column.getIsSorted()
          && (header.column.getIsSorted() === "desc" ? (
            <Box pl="4">
              <TriangleDownIcon color="chakra-subtle-text" aria-label="sorted descending" />
            </Box>
          ) : (
            <Box pl="4">
              <TriangleUpIcon color="chakra-subtle-text" aria-label="sorted ascending" />
            </Box>
          ))}
      </HStack>
    </StyledTh>
  ) : (
    <StyledTh header={header} sx={sx} {...rest}>
      <HStack {...(header.column.columnDef.meta?.headerHStackProps || {})}>
        <Text color="chakra-subtle-text">
          {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
        </Text>
      </HStack>
    </StyledTh>
  );
}
